// React hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Helpers i API servisi
import { transformRoute } from "helpers";
import { getCronJobsAPI, resetCronJobStatusAPI } from "services/api/cronjobs/api";
import { CronJob, CronJobEventType } from "services/api/cronjobs/types";

// Tipovi i konstante
import { CronJobsSearchPageCmp, CronJobsSearchPageProps } from "./types";
import { CRON_JOB_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";
import { CRON_JOBS_COLUMNS } from "./consts";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { NoResults } from "ui/components/NoResults";
import { Button } from "ui/components/Button/Button";
import { Dialog } from "ui/components/Dialog";
import { TableSearch } from "ui/components/Table/TableSearch";

// Moduli specifični za projekt
import { Routes } from "ui/modules/navigation";

/**
 * Popis vrsta obrada. Ima ih nekoliko, pa nema ni paginacije ni filtera — samo osvježavanje,
 * jer se status obrade u tijeku mijenja dok ekran stoji otvoren.
 */
const CronJobsSearchPage: CronJobsSearchPageCmp = (_props: CronJobsSearchPageProps) => {
  const navigate = useNavigate();

  const [cronJobs, setCronJobs] = useState<CronJob[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [resetTarget, setResetTarget] = useState<CronJob | null>(null);
  const [isResetting, setIsResetting] = useState<boolean>(false);

  const fetchCronJobs = useCallback(async () => {
    setIsLoading(true);
    try {
      setCronJobs(await getCronJobsAPI());
    } catch {
      setCronJobs([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCronJobs();
  }, [fetchCronJobs]);

  const navigateToDetail = useCallback(
    (cronJobId: string) => {
      navigate(transformRoute(Routes.CronJobDetailPageRouter, { cronJobId }));
    },
    [navigate]
  );

  const handleEventClick = useCallback(
    (id: string, name: string) => {
      if (name === CronJobEventType.DETAIL) {
        navigateToDetail(id);
      } else if (name === CronJobEventType.RESET_STATUS) {
        setResetTarget(cronJobs.find((job) => job.id === id) ?? null);
      }
    },
    [cronJobs, navigateToDetail]
  );

  const handleResetConfirm = useCallback(async () => {
    if (!resetTarget) return;
    setIsResetting(true);
    try {
      const updated = await resetCronJobStatusAPI(resetTarget.id);
      setCronJobs((prev) => prev.map((job) => (job.id === updated.id ? updated : job)));
    } catch {
      // greška je prikazana kroz snackbar
    } finally {
      setIsResetting(false);
      setResetTarget(null);
    }
  }, [resetTarget]);

  const handleResetCancel = useCallback(() => {
    if (!isResetting) setResetTarget(null);
  }, [isResetting]);

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md border-b border-slate-200">
          <PageHeader>
            <div className="flex items-center justify-between">
              <div>
                <PageTitle
                  title={CRON_JOB_SEARCH_PAGE_TITLE.text}
                  icon={CRON_JOB_SEARCH_PAGE_TITLE.icon}
                  iconColor={CRON_JOB_SEARCH_PAGE_TITLE.iconColor}
                />
                <Breadcrumb />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outlined"
                  startIcon="/media/icons/refresh.svg"
                  onClick={fetchCronJobs}
                  disabled={isLoading}
                >
                  Osvježi
                </Button>
              </div>
            </div>
          </PageHeader>
        </div>
        <div className="px-0 py-6 mx-[-1px] xl:px-6">
          {!isLoading && cronJobs.length === 0 ? (
            <NoResults message="Nema definiranih vrsta obrada" />
          ) : (
            <TableSearch
              columns={CRON_JOBS_COLUMNS}
              data={cronJobs}
              selectableRows={false}
              disablePagination
              isLoading={isLoading}
              onRowClick={navigateToDetail}
              onEventClick={handleEventClick}
            />
          )}
        </div>
      </div>

      <Dialog
        open={resetTarget !== null}
        title="Resetiraj status vrste obrade"
        message={
          resetTarget
            ? `Vrsta obrade "${resetTarget.name}" vraća se iz statusa '${resetTarget.status}' u 'finished', pa je planer ponovno smije pokrenuti. Otvorena obrada bit će označena kao prekinuta. Koristite samo ako je obrada zapela.`
            : ""
        }
        confirmText={isResetting ? "Resetiram…" : "Resetiraj"}
        cancelText="Odustani"
        onConfirm={handleResetConfirm}
        onCancel={handleResetCancel}
      />
    </>
  );
};

CronJobsSearchPage.route = Routes.CronJobSearchPageRouter;

export default CronJobsSearchPage;
